import { CheckIcon } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import type { EventRsvpCardProps } from '@/lib/types/events.types';
import { EventDetailsContent } from '@/content/EventDetailsContent';

export function EventRsvpCard({
  currentStatus,
  deadlinePassed,
  atCapacity,
  deadlineText,
  isLoading,
  isAttended = false,
  onGoingClick,
  onNotGoingClick,
  isSeriesEvent = false,
}: EventRsvpCardProps) {
  const isGoing = currentStatus === 'yes';
  const isNotGoing = currentStatus === 'no';
  const goingDisabled =
    isAttended || deadlinePassed || (atCapacity && !isGoing);
  const notGoingDisabled = isAttended || deadlinePassed;

  return (
    <div
      data-testid="event-rsvp-card"
      className="w-full rounded-2xl bg-slate-700 px-5 py-4 mt-4"
    >
      <h3>RSVP</h3>
      <div className="flex items-center gap-0 mt-2">
        <Button
          type="button"
          data-testid="rsvp-going-button"
          data-selected={isGoing}
          variant={isGoing ? 'default' : 'outline'}
          onClick={onGoingClick}
          disabled={goingDisabled || isLoading}
          className={cn(
            'flex-1 h-12 relative rounded-r-none -mr-[1px]',
            isGoing && 'z-10 shadow-sm'
          )}
        >
          {isLoading && isGoing ? (
            'Loading...'
          ) : (
            <span className="flex items-center gap-2">
              {isGoing && <CheckIcon size={20} weight="fill" />}
              {EventDetailsContent.rsvpGoing}
            </span>
          )}
        </Button>
        <Button
          type="button"
          data-testid="rsvp-not-going-button"
          data-selected={isNotGoing}
          variant={isNotGoing ? 'default' : 'outline'}
          onClick={onNotGoingClick}
          disabled={notGoingDisabled || isLoading}
          className={cn(
            'flex-1 h-12 relative rounded-l-none',
            isNotGoing && 'z-10 shadow-sm'
          )}
        >
          {isLoading && isNotGoing ? (
            'Loading...'
          ) : (
            <span className="flex items-center gap-2">
              {isNotGoing && <CheckIcon size={20} weight="fill" />}
              {EventDetailsContent.rsvpNotGoing}
            </span>
          )}
        </Button>
      </div>
      {isAttended && (
        <p
          data-testid="rsvp-attended-note"
          className="text-small text-slate-400 mt-3"
        >
          {EventDetailsContent.rsvpAttendedNote}
        </p>
      )}
      {deadlineText && (
        <p className="text-small text-slate-400 mt-3 ">{deadlineText}</p>
      )}
      {isSeriesEvent && (
        <p className="text-small text-slate-400 mt-3">
          Your RSVP applies to all events in this series
        </p>
      )}
    </div>
  );
}
